import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Pagination from '../Paginations'
import { createPayout } from '../../api/payouts'
import { updateUser, getUser } from '../../api/user'
import { CircularProgress, Alert, Snackbar } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PayoutTable from '../shared/PayoutTable'

const Payouts = () => {

    const uid = localStorage.getItem('uid')
    const [balance, setBalance] = useState(0)
    const [totalPaid, setTotalPaid] = useState(0)
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' })
    const [payouts, setPayouts] = useState([])
    const [paymentSetup, setPaymentSetup] = useState(false)
    const [setupRequest, setSetupRequest] = useState(false)
    const { 
        data: user = {}, 
        isLoading,
       error 
    } = useQuery({
        queryKey: ['artists', uid],
        queryFn: () => getUser(uid),
        select: (data) => data || {},
    })
    const queryClient = useQueryClient();
    useEffect(() => {
        if (user && !isLoading) {
            setBalance(user?.balance?.toFixed(2) || 1000);
            setTotalPaid(user?.total_paid?.toFixed(2) || 0);
            setPayouts(user?.payouts || []);
            setPaymentSetup(user?.payment_setup !== undefined ? user.payment_setup : false);
            setSetupRequest(user?.setup_request !== undefined ? user.setup_request === "true" ? true : false : false);
        }
    }, [user, isLoading]);
    
    const statusClasses = {
        APPROVED: 's-green',
        PENDING: 's-orange',
        DECLINED: 's-red n-right'
    }
    const mapClasess = {
        APPROVED: "Approved",
        PENDING: "Pending",
        DECLINED: "Declined"
    }
    const createPayoutMutation = useMutation({
        mutationFn: (payoutData) => createPayout(payoutData),
        onSuccess: () => {
            // Invalidate and refetch the user data to update the UI
            setNotification({
                open: true,
                message: 'Payment successfully requested',
                type: 'success'
            })
            queryClient.invalidateQueries(['artists', uid]);
            // Optionally, you can show a success message or perform other actions
            successBtn.current.click();
        },
        onError: (error) => {
            setNotification({
                open: true,
                message: 'Payment successfully requested',
                type: 'error'
            })
            // Handle error, e.g., show a notification
            console.error("Error creating payout:", error);
        }
    });

    // Define the mutation for updating the user
    const updateUserMutation = useMutation({
        mutationFn: (userData) => updateUser(userData.uid, userData),
        onSuccess: () => {
            setNotification({
                open: true,
                message: 'Payment setup request sent',
                type: 'success'
            })
            // Invalidate and refetch the user data to update the UI
            queryClient.invalidateQueries(['artists', uid]);
            // Optionally, you can show a success message or perform other actions
            setupSuccessBtn.current.click();
        },
        onError: (error) => {
            // Handle error, e.g., show a notification
            setNotification({
                open: true,
                message: 'Payment setup request failed',
                type: 'error'
            })
            
        }
    });
    
    const requestPayout = () => {
        createPayoutMutation.mutate({
            userProfileId: uid,
            method: paymentMethod,
            amount: Number(balance),
            currency: 'USD'
        });
    }
    const sendSetupRequest = () => {
        updateUserMutation.mutate({ uid, setup_request: "true" });
    }
    const successBtn = useRef()
    const pendingModalBtn = useRef()
    const insufficientBalanceBtn = useRef()
    const requestModalBtn = useRef()
    const [paymentMethod, setPaymentMethod] = useState('Bank Transfer')
    const [amount, setAmount] = useState(0)

    // Function to check if there is a pending withdrawal
    const checkPendingWithdraw = () => {
        // First check if user has minimum balance of $100
        if (balance < 100) {
            insufficientBalanceBtn.current.click();
            return;
        }
        
        // Then check for pending withdrawals
        const hasPendingWithdraw = payouts.some(payout => payout.status === 'PENDING');
        
        if (hasPendingWithdraw) {
            // If there's a pending withdrawal, show the pending modal
            pendingModalBtn.current.click();
        } else {
            // If no pending withdrawal, show the request modal
            requestModalBtn.current.click();
        }
    }

    const currentDate = () => {
        const day = String(new Date().getDate()).padStart(2, '0'); // Get the day and pad with leading zero if needed
        const month = String(new Date().getMonth() + 1).padStart(2, '0'); // Get the month (0-indexed) and pad with leading zero
        const year = new Date().getFullYear(); // Get the full year
      
        return `${year}/${month}/${day}`; // Construct the "dd/mm/yyyy" format
    };
    const paymentMethods = [
        'Wire Transfer',
        'Paypal',
        'MFS',
        'ACH'
    ]
   

    const setupSuccessBtn = useRef()
    


    /////////// PAGINATION ///////////
    const itemsPerPage = 25;
    const [currentPage, setCurrentPage] = useState(1);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = payouts.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = Math.ceil(payouts.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    
  return (
    <div className='dash-section'>
        <h1 className='dash-title'>Payouts</h1>
        <div className="row">
            <div className="col-lg-7 mt-4">
                <div className="dash-card">
                    <div className="payouts-widget-info">
                        <h6>Total Paid:</h6>
                        <span>${totalPaid}</span>
                    </div>
                    <div className="payouts-widget-info">
                        <h6>Available Balance:</h6>
                        <span>${balance}</span>
                    </div>
                    <Link 
                        className="dash-card-heading-btn mt-4 w-100 d-flex justify-content-center mb-0"
                        onClick={checkPendingWithdraw}
                    >
                        Request Withdraw
                    </Link>
                </div>
            </div>
            <div className="col-lg-5 mt-4">
                <div className="dash-card payout-method-card">
                    <h5>Manage Payout Methods</h5>
                    <h6>Add or update your details</h6>
                    {
                        setupRequest === false && paymentSetup === true && <a href={'https://suppliers.tipalti.com/Qinetic/account/login'} target='blank'>Update Details</a>
                    }
                    {
                        setupRequest !== true && setupRequest !== 'Sent' && paymentSetup === false && <Link to={'#'} onClick={sendSetupRequest}>Add Payment Details</Link>
                    }
                    {
                        setupRequest === true && (
                            <div className="payout-check">
                                <i className="fi fi-ts-check-circle"></i>
                                Setup request sent!
                            </div>
                        )
                    }
                    {
                        setupRequest === 'Sent' && (
                            <div className="payout-check">
                                <i className="fi fi-ts-check-circle"></i>
                                You received setup invite!
                            </div>
                        )
                    }
                    {
                        paymentSetup && setupRequest === false && (
                            <div className="payout-check">
                                <i className="fi fi-ts-check-circle"></i>
                                You have a payout account set up
                            </div>
                        )
                    }
                </div>
            </div>
            <div className="col-12 mt-4">
                <div className="dash-card">
                    <div className="dash-card-heading">
                        <h2 className="dash-card-title">
                            Withdraws
                        </h2>
                        {/* <div className="dash-card-heading-right">
                            <h6>Balance: <span>${balance}</span></h6>
                            <Link 
                                className="dash-card-heading-btn"
                                data-bs-toggle="modal"
                                data-bs-target="#requestModal"
                            >
                                Request Withdraw
                            </Link>
                        </div> */}
                    </div>
                    
                    <PayoutTable 
                        payouts={currentItems}
                        isLoading={isLoading}
                        error={error}
                        emptyMessage="No withdrawals available"
                        isPending={false}
                        isAdmin={false}
                    />
                    
                    {/* <div className="table-pagination">
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                        <button key={pageNumber} className={currentPage === pageNumber && 'active'} onClick={() => handlePageChange(pageNumber)}>
                            {pageNumber}
                        </button>
                        ))}
                    </div> */}
                    <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
                </div>
            </div>
        </div>

        {/* Hidden button to trigger the pending modal */}
        <button data-bs-toggle="modal" data-bs-target="#pendingModal" ref={pendingModalBtn} hidden></button>
        
        {/* Hidden button to trigger the insufficient balance modal */}
        <button data-bs-toggle="modal" data-bs-target="#insufficientBalanceModal" ref={insufficientBalanceBtn} hidden></button>
        
        {/* Hidden button to trigger the request modal */}
        <button data-bs-toggle="modal" data-bs-target="#requestModal" ref={requestModalBtn} hidden></button>
        
        {/* Insufficient Balance Modal */}
        <div className="modal fade" id="insufficientBalanceModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="insufficientBalanceModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button className='success-close-btn btn' data-bs-dismiss="modal" aria-label="Close">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-info-circle text-warning error-icon"></i>
                        <h5>Minimum balance required: $100</h5>
                        <p>Your current balance is ${balance}. You need at least $100 to request a withdrawal.</p>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Pending Withdrawal Modal */}
        <div className="modal fade" id="pendingModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="pendingModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button className='success-close-btn btn' data-bs-dismiss="modal" aria-label="Close">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-info-circle text-warning error-icon"></i>
                        <h5>You already have a pending withdrawal request</h5>
                        <p>Please wait for your current request to be processed before submitting a new one.</p>
                    </div>
                </div>
            </div>
        </div>

        <div className="modal fade" id="requestModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="requestModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-body">
                        <div className="modal-top mb-4">
                            <h5>Request Withdraw</h5>
                            <i className="fi fi-rr-cross" data-bs-dismiss="modal"></i>
                        </div>
                        <div className="row">
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Payment Method</label>
                                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className='dash-form-select'>
                                        {paymentMethods.map((method, i) => (
                                            <option key={i} value={method}>{method}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6 mt-3">
                                <div className="dash-form-group">
                                    <label>Amount ($)</label>
                                    <p className='dash-form-input'>{balance}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-end mt-4">
                            <button 
                                className={`dash-form-btn`} 
                                data-bs-dismiss="modal"
                                onClick={requestPayout}
                            >Submit</button>
                            <button data-bs-toggle="modal" data-bs-target="#successModal" ref={successBtn} hidden></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button className='success-close-btn btn' data-bs-dismiss="modal" aria-label="Close">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Payout Request Sent!</h5>
                    </div>
                </div>
            </div>
        </div>

        <button data-bs-toggle="modal" data-bs-target="#setupSuccessModal" ref={setupSuccessBtn} hidden></button>
        <div className="modal fade" id="setupSuccessModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="setupSuccessModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button className='success-close-btn btn' data-bs-dismiss="modal" aria-label="Close">
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Setup Request Sent!</h5>
                        <p>You'll receive an email within 24 hours</p>
                    </div>
                </div>
            </div>
        </div>
         {/* Notification Snackbar */}
         <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.type}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
    </div>
  )
}

export default Payouts